/** @typedef {'welcome' | 'discovery' | 'show_projects' | 'select_config' | 'form' | 'submitted' | 'clarify_project' | string} AiStep */

import { buildProjectDetailPathFromItem, PDP_MODE } from "./pdpRoutes";

/**
 * @typedef {Object} AiChatFilters
 * @property {string} city
 * @property {'apartment' | 'villa' | 'plot' | string} [property_type]
 * @property {'buy' | 'rent' | 'resale' | string} [listing_type]
 * @property {string} [location]
 * @property {number} [budget_min]
 * @property {number} [budget_max]
 * @property {number} [budget_max_cr]
 * @property {string} [bhk]
 * @property {string} [construction_status]
 * @property {number} [min_area_sqft]
 * @property {number} [max_area_sqft]
 * @property {number} [min_price_per_sqft]
 * @property {number} [max_price_per_sqft]
 * @property {'gated' | 'standalone' | 'high_rise' | string} [structural_type]
 * @property {string[]} [amenities]
 * @property {'buy' | 'invest' | 'family' | 'rent' | string} [intent_goal]
 * @property {number[]} [shortlist]
 * @property {number|null} [locked_project_id]
 * @property {number} [slot_version]
 */

/**
 * @typedef {Object} AiChatContext
 * @property {AiStep} step
 * @property {number} [selected_project_id]
 * @property {string} [configuration_label]
 * @property {Record<string, string>} [form_data]
 * @property {AiChatFilters} filters
 */

/**
 * @typedef {Object} AiProjectCard
 * @property {number} id
 * @property {string} [title]
 * @property {string} [slug]
 * @property {string} [price_label]
 * @property {string} [image_url]
 * @property {string} [deep_link]
 * @property {string} [city]
 * @property {string} [geo_address]
 * @property {string} [location]
 * @property {string[]} [why]
 * @property {string[]} [reason_codes]
 * @property {'in_budget' | 'stretch' | string} [tier]
 */

/**
 * @typedef {Object} AiLocalityCard
 * @property {string} [name]
 * @property {string} [title]
 * @property {string} [slug]
 * @property {string} [deep_link]
 * @property {string} [image_url]
 */

/**
 * @typedef {Object} AiArticleCard
 * @property {string} [title]
 * @property {string} [slug]
 * @property {string} [deep_link]
 * @property {string} [image_url]
 */

/**
 * @typedef {Object} AiMessageResponse
 * @property {string} reply
 * @property {AiStep} [step]
 * @property {string} [session_id]
 * @property {boolean} [lead_submitted]
 * @property {string} [user_first_name]
 * @property {Record<string, string>} [deep_link_templates]
 * @property {AiProjectCard[]} [projects]
 * @property {AiLocalityCard[]} [hot_localities]
 * @property {AiArticleCard[]} [articles]
 * @property {string[]} [quick_replies]
 * @property {string[]} [config_options]
 * @property {Object|null} [comparison]
 * @property {boolean} [comparison_only]
 * @property {unknown[]} [recommendations]
 * @property {AiChatFilters} [filters]
 * @property {number|null} [selected_project_id]
 * @property {string|null} [configuration_label]
 * @property {string[]} [form_required]
 * @property {Record<string, string>} [form_data]
 * @property {string|null} [rank_by]
 * @property {boolean} [agent_mode]
 * @property {string} [intent]
 * @property {string} [routing_path]
 */

/**
 * @typedef {Object} AiTranscriptMessage
 * @property {string} id
 * @property {'user' | 'assistant'} role
 * @property {string} text
 * @property {AiMessageResponse} [response]
 */

export const AI_SESSION_KEY = "ai_session_id";
export const AI_CLIENT_CHANNEL = "web";
export const AI_LEAD_SOURCE = "ai_assistant_web";
export const DEFAULT_CITY_SLUG = "hyderabad";
export const AI_EMPTY_REPLY_FALLBACK =
  "I couldn't generate a reply. Try rephrasing or tap a suggestion below.";

export const AI_STARTER_CHIPS = [
  "3 BHK in Gachibowli under 1.5 crore",
  "Ready to move flats in Kokapet",
  "Compare two projects",
  "What is RERA?",
  "Ultra-luxury in Kokapet above 5 crores",
];

export const AI_STEP_CLARIFY_PROJECT = "clarify_project";

/** @param {string} city @param {{ property_type?: string, listing_type?: string }} [routeFilters] */
export function createDefaultAiContext(city = DEFAULT_CITY_SLUG, routeFilters = {}) {
  const filters = {
    city: String(city || DEFAULT_CITY_SLUG).trim().toLowerCase() || DEFAULT_CITY_SLUG,
  };
  if (routeFilters.property_type) filters.property_type = routeFilters.property_type;
  if (routeFilters.listing_type) filters.listing_type = routeFilters.listing_type;
  return {
    step: "welcome",
    filters,
  };
}

export function createEphemeralAiSessionId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `web-${crypto.randomUUID()}`;
  }
  return `web-${Date.now()}`;
}

/** Remove any legacy persisted session from a previous build. */
export function clearStoredAiSessionId() {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(AI_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

/** @deprecated Use createEphemeralAiSessionId — chats are not persisted across refresh. */
export function createNewAiSessionId() {
  clearStoredAiSessionId();
  return createEphemeralAiSessionId();
}

/** @deprecated Chats are ephemeral; always creates a new in-memory session id. */
export function getOrCreateAiSessionId() {
  clearStoredAiSessionId();
  return createEphemeralAiSessionId();
}

/**
 * Merge API response into previous context per chat-website-integration §5.2.
 * @param {AiChatContext} prev
 * @param {AiMessageResponse} res
 * @param {string} [city]
 */
export function mergeContext(prev, res, city = DEFAULT_CITY_SLUG) {
  const prevCtx = prev || createDefaultAiContext(city);
  const citySlug =
    String(res?.filters?.city || prevCtx.filters?.city || city || DEFAULT_CITY_SLUG)
      .trim()
      .toLowerCase() || DEFAULT_CITY_SLUG;

  return {
    step: res?.step ?? prevCtx.step,
    selected_project_id: res?.selected_project_id ?? prevCtx.selected_project_id,
    configuration_label: res?.configuration_label ?? prevCtx.configuration_label,
    form_data: res?.form_data ?? prevCtx.form_data,
    filters: mergeBrainV2Filters(prevCtx.filters, res?.filters, citySlug),
  };
}

/**
 * Merge Brain v2 slot filters — server response is source of truth for set keys.
 * @param {AiChatFilters|undefined} prevFilters
 * @param {AiChatFilters|undefined} resFilters
 * @param {string} citySlug
 */
export function mergeBrainV2Filters(prevFilters, resFilters, citySlug) {
  const prev = prevFilters && typeof prevFilters === "object" ? prevFilters : {};
  const res = resFilters && typeof resFilters === "object" ? resFilters : {};
  return {
    ...prev,
    ...res,
    city: citySlug,
  };
}

/** @param {AiMessageResponse} response @param {string} city @param {AiChatContext} [prev] */
export function buildContextFromResponse(response, city, prev) {
  const base = prev || createDefaultAiContext(city);
  return mergeContext(base, response || {}, city);
}

/**
 * @param {AiProjectCard} project
 * @param {string} [propertyType]
 * @param {string} [mode]
 */
export function buildAiProjectPath(project, propertyType = "apartment", mode = PDP_MODE.LISTING) {
  if (project?.deep_link) return project.deep_link;
  if (!project?.slug) return null;
  const ptype = String(propertyType || "apartment").toLowerCase();
  return buildProjectDetailPathFromItem(project, ptype, mode);
}

/**
 * Build prefetch `type` query param e.g. apartment_buy.
 * @param {{ property_type?: string, listing_type?: string }} routeFilters
 */
export function buildPrefetchListingType(routeFilters = {}) {
  const property = String(routeFilters.property_type || "apartment").toLowerCase();
  let listing = String(routeFilters.listing_type || "buy").toLowerCase();
  if (listing === "resale") listing = "buy";
  return `${property}_${listing}`;
}

/** @param {string} reply */
export function normalizeAssistantReply(reply) {
  const text = String(reply ?? "").trim();
  return text || AI_EMPTY_REPLY_FALLBACK;
}

/** @param {unknown} comparison */
export function hasAiComparisonPayload(comparison) {
  if (!comparison || typeof comparison !== "object") return false;
  if (
    Array.isArray(comparison.fields) &&
    Array.isArray(comparison.data) &&
    comparison.data.length >= 2
  ) {
    return true;
  }
  return Array.isArray(comparison.projects) && comparison.projects.length >= 2;
}

/** Parse "Name: John, Phone: 9876543210" into form_data keys. */
export function parseLeadFormMessage(message) {
  const data = {};
  String(message || "")
    .split(",")
    .forEach((part) => {
      const idx = part.indexOf(":");
      if (idx <= 0) return;
      const label = part.slice(0, idx).trim().toLowerCase().replace(/\s+/g, "_");
      const value = part.slice(idx + 1).trim();
      if (label && value) data[label] = value;
    });
  return data;
}

export function createMessageId() {
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function isAiAssistantEnabled() {
  return process.env.REACT_APP_ENABLE_AI_ASSISTANT !== "false";
}
